import { Avatar, Flex, type SelectProps, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useEffect, useMemo, useState } from "react"

import SelectComponent from "../../../../../components/Select"
import useDebounce from "../../../../../hooks/use-debounce"
import { searchEvents } from "../../../api/services.ts"
import { type CalendarFilterParticipant } from "../../../redux/calendar.types.ts"

const { Text } = Typography

const MIN_SEARCH_LENGTH = 3
const DEBOUNCE_DELAY = 500

interface CalendarFiltersSearchProps {
  value: CalendarFilterParticipant[] | null
  onSelectParticipants: (p: CalendarFilterParticipant[]) => void
}

export function CalendarFiltersTeamMembers({
  value,
  onSelectParticipants
}: CalendarFiltersSearchProps) {
  const [search, setSearch] = useState<string>("")
  const [items, setItems] = useState<BaseOptionType[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [hasSearched, setHasSearched] = useState<boolean>(false)

  const debouncedSearch = useDebounce(search, DEBOUNCE_DELAY)

  const allItemsMap = useMemo(() => {
    const merged = [...(value || []), ...(items || [])]
    return new Map(merged.map((item) => [item.value, item]))
  }, [value, items])

  const onSearchDependenciesHandler = async (q: string) => {
    try {
      if (q?.trim()?.length < MIN_SEARCH_LENGTH) {
        setLoading(false)
        setHasSearched(false)
        return
      }

      setLoading(true)
      const response = await searchEvents({ q })
      if (!response?.ok) throw new Error("")

      const resParticipants = response?.data?.participants || []
      setItems(
        resParticipants?.map((p) => ({
          value: p?.id,
          label: p?.label || "-",
          email: p?.email || "-"
        }))
      )
      setHasSearched(true)
    } catch (e) {
      console.error(e)
      setItems([])
      setHasSearched(true)
    } finally {
      setLoading(false)
    }
  }

  const onChange: SelectProps["onChange"] = (ids: string[]) => {
    const participants = ids.map((id) => {
      const item = allItemsMap.get(id)
      return {
        value: id,
        label: item?.label || "No name",
        email: item?.email || "-"
      }
    })

    onSelectParticipants(participants)
  }

  const onSearchHandler = (value: string) => {
    setSearch(value)

    // Показуємо індикатор завантаження одразу при валідному пошуку
    if (value?.trim()?.length >= MIN_SEARCH_LENGTH) {
      setLoading(true)
    }
  }

  // Кастомний контент для різних станів
  const notFoundContent = useMemo(() => {
    const trimmedSearch = search?.trim()

    // Якщо немає пошукового запиту
    if (!trimmedSearch) {
      return (
        <div className="text-center py-3 text-gray-500">
          <div className="text-sm">Start typing to search team members</div>
        </div>
      )
    }

    // Якщо запит занадто короткий
    if (trimmedSearch.length < MIN_SEARCH_LENGTH) {
      const remaining = MIN_SEARCH_LENGTH - trimmedSearch.length
      return (
        <div className="text-center py-3 text-gray-500">
          <div className="text-sm">
            Enter at least {remaining} more character{remaining > 1 ? "s" : ""}{" "}
            to search
          </div>
        </div>
      )
    }

    // Якщо йде завантаження
    if (loading) {
      return (
        <div className="text-center py-3 text-gray-500">
          <div className="text-sm">Searching...</div>
        </div>
      )
    }

    // Якщо пошук виконано, але результатів немає
    if (
      hasSearched &&
      items.length === 0 &&
      debouncedSearch === trimmedSearch
    ) {
      return (
        <div className="text-center py-3 text-gray-500">
          <div className="text-sm font-medium">No team members found</div>
          <div className="text-xs mt-1">
            Try searching with different keywords
          </div>
        </div>
      )
    }

    // Якщо є результати - повертаємо null, щоб показувались опції
    return null
  }, [search, debouncedSearch, loading, hasSearched, items.length])

  useEffect(() => {
    void onSearchDependenciesHandler(debouncedSearch)
  }, [debouncedSearch])

  // Очищаємо items при очищенні пошуку
  useEffect(() => {
    if (!search?.trim()) {
      setItems(value || [])
      setHasSearched(false)
    }
  }, [search])

  const onOpenChange = (open: boolean) => {
    if (open) {
      setItems(value || [])
      setSearch("")
      setHasSearched(false)
      setLoading(false)
    }
  }
  return (
    <SelectComponent
      className={"min-w-44"}
      placeholder={"All Team Members"}
      onChange={onChange}
      value={value}
      showSearch
      mode={"multiple"}
      onSearch={onSearchHandler}
      filterOption={false}
      loading={loading}
      options={items}
      maxTagCount={"responsive"}
      notFoundContent={notFoundContent}
      onOpenChange={onOpenChange}
      optionRender={(props) => {
        const data = props?.data

        return (
          <Flex align="center" gap={10}>
            <Avatar className={"bg-primary shrink-0"}>
              {data?.label?.charAt(0)}
            </Avatar>
            <Flex vertical className={"overflow-hidden"}>
              <Text strong>{data?.label}</Text>
              <Text
                type="secondary"
                style={{ fontSize: 12 }}
                ellipsis={{ tooltip: data?.email }}
              >
                {data?.email}
              </Text>
            </Flex>
          </Flex>
        )
      }}
      popupRender={(menu) => {
        // Якщо є кастомний контент і немає опцій - показуємо тільки контент
        if (notFoundContent && items.length === 0) {
          return <div className="px-2">{notFoundContent}</div>
        }
        return menu
      }}
    />
  )
}
